import React from "react";
import moment from "moment";
import "moment/locale/sv";
import { Badge, Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { FileEarmarkArrowDown } from "react-bootstrap-icons";
import { Link } from "react-router-dom";
import { DateField } from "../forms/dateField";
import { SelectField } from "../forms/selectField";
import { SwitchField } from "../forms/switchField";
import { TextField } from "../forms/textField";
import { CarBrand } from "../../models/responses/carBrand";
import { ClientType } from "../../models/enums/clientTypeEnum";
import { Customer } from "../../models/responses/customer";
import { Order } from "../../models/responses/order";
import { OrderStatus } from "../../models/responses/orderStatus";
import { OrderType } from "../../models/responses/orderType";
import { Supplier } from "../../models/responses/supplier";

// ============================================
// INTERFACES
// ============================================

export interface OrderFieldsHandlers {
  onAoNumberChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onApprovedChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onArriveDateChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onAssignedToChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBookingDateChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onCarBrandChange?: (carBrandId: string) => void;
  onCarInfoChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClientExternalOrderIdChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClientVehicleRetrieverChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onCommentChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onCustomerChange?: (customerId: string) => void;
  onDeliveryDateChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDeniedChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDiscountChange?: (discount: number | null) => void;
  onEmailBodyChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFilenameChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFollowUpChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onInternalCommentChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onOdometerChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onOrderDateChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onOrderedByChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onOrderTypeChange?: (orderTypeId: string) => void;
  onPickupDateChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPriceProposalMessageChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPurchaseOrderNumberChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRegistrationNumberChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onStatusChange?: (statusId: string) => void;
  onSupplierChange?: (supplierId: string | null) => void;
  onVinChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onWarehouseLocationChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onWorkshopIdChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onWorkshopNameChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export interface OrderFieldsLists {
  carBrands?: CarBrand[];
  customers?: Customer[];
  orderStatusList?: OrderStatus[];
  orderTypes?: OrderType[];
  suppliers?: Supplier[];
}

export interface OrderFieldsLoadingStates {
  isLoadingCarBrands?: boolean;
  isLoadingCustomers?: boolean;
  isLoadingOrderStatuses?: boolean;
  isLoadingOrderTypes?: boolean;
  isLoadingSuppliers?: boolean;
}

interface OrderFieldsProps {
  orderData: Order;
  orderStatus: OrderStatus;
  clientType: ClientType;
  order: Order;
  onShowCustomerModal: () => void;
  mode?: "view" | "edit" | "add";
  handlers?: OrderFieldsHandlers;
  lists?: OrderFieldsLists;
  loadingStates?: OrderFieldsLoadingStates;
  wrapInForm?: boolean;
}

// ============================================
// MAIN COMPONENT
// ============================================

const OrderFields: React.FC<OrderFieldsProps> = ({
  orderData,
  orderStatus,
  clientType,
  order,
  onShowCustomerModal,
  mode = "view",
  handlers = {},
  lists = {},
  loadingStates = {},
  wrapInForm = true,
}) => {
  moment.locale("sv");
  moment.updateLocale(moment.locale(), { invalidDate: "-" });

  const isEditMode = mode === "edit" || mode === "add";
  const isAddMode = mode === "add";
  const isExported = orderData.exported;
  const pageType = orderStatus.orderPageType;

  // Helper to check field visibility
  const showField = (field: string, alwaysInAdd = true): boolean => {
    if (isAddMode && alwaysInAdd) return true;
    return !!(pageType && (pageType as unknown as Record<string, boolean>)[field]);
  };

  const isStandard = clientType === ClientType.Standard;
  const isCatalog = clientType === ClientType.Catalog;
  const isFleet = clientType === ClientType.Fleet;

  const content = (
    <>
      {/* STATUS ROW */}
      <Row>
        <SelectField
          label="Status"
          value={isEditMode ? orderData.orderStatusId : orderData.orderStatus?.name}
          options={lists.orderStatusList || []}
          getOptionValue={(s) => s.id}
          getOptionLabel={(s) => s.name}
          colSize={isEditMode ? 6 : 4}
          isEditable={isEditMode && !!lists.orderStatusList}
          onChange={handlers.onStatusChange}
          isLoading={loadingStates.isLoadingOrderStatuses}
        />
        {orderData.approved && isCatalog && (
          <Col xs={12} md={3}>
            <div className="export-badge">
              <Badge bg="success">Ordern är godkänd i katalogen</Badge>
            </div>
          </Col>
        )}
        <Col xs={12} md={2}>
          {orderData.exported && (
            <div className="export-badge">
              <Badge bg="success">Ordern är exporterad till AX</Badge>
            </div>
          )}
        </Col>
        <Col xs={12} md={5}>
          {pageType?.fileName && orderData.filename && (
            <Button
              variant="outline-primary"
              className="mt-2 ms-2 float-end"
              target="_blank"
              href={orderData.filepath}
            >
              Hämta orderfil <FileEarmarkArrowDown />
            </Button>
          )}
        </Col>
      </Row>

      {/* CUSTOMER & BASIC INFO */}
      <Row>
        {showField("customer") && (
          <Col xs={12} md={4}>
            <Form.Group className="mb-3">
              <Form.Label>
                Kund{" "}
                {!isAddMode && (
                  <Badge bg="primary" onClick={onShowCustomerModal} role="button">
                    Visa kundinfo
                  </Badge>
                )}
                {isEditMode && loadingStates.isLoadingCustomers && (
                  <Spinner variant="primary" size="sm" className="ms-2" />
                )}
              </Form.Label>
              {isEditMode && lists.customers ? (
                <Form.Select
                  aria-label="Kund"
                  value={orderData.customerId || ""}
                  onChange={(e) => handlers.onCustomerChange?.(e.target.value)}
                  disabled={isExported}
                >
                  {isAddMode && <option value="">Välj kund</option>}
                  {lists.customers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}{!isAddMode && ` - ${c.externalId}`}
                    </option>
                  ))}
                </Form.Select>
              ) : (
                <Form.Control type="text" value={orderData.customer?.name || ""} readOnly disabled />
              )}
            </Form.Group>
          </Col>
        )}

        {showField("assignedTo") && (
          <TextField
            label="Ansvarig"
            value={orderData.assignedTo}
            isEditable={isEditMode}
            onChange={handlers.onAssignedToChange}
          />
        )}

        {showField("registrationNumber") && (
          <TextField
            label="Registreringsnummer"
            value={orderData.registrationNumber}
            colSize={2}
            isEditable={isEditMode}
            isDisabled={isExported}
            onChange={handlers.onRegistrationNumberChange}
            required={isEditMode}
            requiredBadge={
              !isAddMode && !orderData.registrationNumber && (
                <Badge bg="danger">Krävs för export</Badge>
              )
            }
          />
        )}

        {isCatalog && (
          <SelectField
            label="Bilmärke"
            value={isEditMode ? orderData.selectedCarBrandId : orderData.selectedCarBrand?.name}
            options={lists.carBrands || []}
            getOptionValue={(b) => b.id}
            getOptionLabel={(b) => b.name}
            colSize={2}
            isEditable={isEditMode && !!lists.carBrands}
            onChange={handlers.onCarBrandChange}
            isLoading={loadingStates.isLoadingCarBrands}
            placeholder="- Inget bilmärke valt -"
          />
        )}
      </Row>

      {/* VIN & CAR INFO */}
      <Row>
        {showField("vin") && (
          <Col xs={12} md={4}>
            <Form.Group className="mb-3">
              <Form.Label>
                VIN
                {!isAddMode && (
                  <>
                    {" "}
                    <Badge bg={orderData.vinVerified ? "success" : "warning"}>
                      {orderData.vinVerified ? "Kontrollerad" : "Inte kontrollerad"}
                    </Badge>
                    {orderData.correctedVin && (
                      <Badge bg="success" className="ms-1">VIN har korrigerats</Badge>
                    )}
                  </>
                )}
              </Form.Label>
              <Form.Control
                type="text"
                value={orderData.vin}
                readOnly={!isEditMode}
                disabled={!isEditMode}
                onChange={isEditMode ? handlers.onVinChange : undefined}
              />
            </Form.Group>
          </Col>
        )}

        {showField("carInfo") && (
          <TextField
            label="Bilmärke"
            value={orderData.carInfo}
            isEditable={isEditMode}
            onChange={handlers.onCarInfoChange}
          />
        )}

        {isStandard && showField("aoNumber") && (
          <TextField
            label="AO-nummer"
            value={orderData.aoNumber}
            isEditable={isEditMode}
            isDisabled={isExported}
            onChange={handlers.onAoNumberChange}
            required={isEditMode}
            requiredBadge={
              !isAddMode && !orderData.aoNumber && <Badge bg="danger">Krävs för export</Badge>
            }
          />
        )}

        {isCatalog && showField("externalOrderId", false) && (
          <TextField
            label="Order.nr"
            value={orderData.externalOrderId || ""}
            requiredBadge={
              (!orderData.externalOrderId || orderData.externalOrderId === 0) && (
                <Badge bg="danger">Krävs för export</Badge>
              )
            }
          />
        )}
      </Row>

      {/* ORDER DETAILS */}
      <Row>
        {showField("foNumber", false) && (
          <TextField label="FO-nummer" value={orderData.salesOrderNumber} />
        )}

        {showField("ioNumber", false) && (
          <TextField
            label="IO-nummer"
            value={orderData.purchaseOrderNumber}
            isEditable={isEditMode}
            onChange={handlers.onPurchaseOrderNumberChange}
          />
        )}

        {showField("orderType") && (
          <>
            <SelectField
              label="Ordertyp"
              value={isEditMode ? orderData.orderTypeId : orderData.orderType?.name}
              options={lists.orderTypes || []}
              getOptionValue={(t) => t.id}
              getOptionLabel={(t) => t.name}
              colSize={2}
              isEditable={isEditMode && !!lists.orderTypes}
              onChange={handlers.onOrderTypeChange}
              isLoading={loadingStates.isLoadingOrderTypes}
              placeholder="- Ingen ordertyp vald -"
            />
            {isStandard && (
              <Col xs={12} md={2}>
                <Form.Group className="mb-3">
                  <Form.Label>Rabatt (%)</Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    max={100}
                    value={orderData.discount ?? ""}
                    readOnly={!isEditMode}
                    disabled={!isEditMode}
                    onChange={isEditMode ? (e) => {
                      const val = e.target.value === "" ? null : Number(e.target.value);
                      if (val === null || (val >= 0 && val <= 100)) {
                        handlers.onDiscountChange?.(val);
                      }
                    } : undefined}
                  />
                </Form.Group>
              </Col>
            )}
          </>
        )}

        {showField("orderedBy", false) && (
          <TextField
            label="Beställd av"
            value={orderData.orderedBy}
            isEditable={isEditMode}
            onChange={handlers.onOrderedByChange}
          />
        )}

        {showField("followUp", false) && (
          <TextField
            label="Uppföljning"
            value={orderData.followUp}
            isEditable={isEditMode}
            onChange={handlers.onFollowUpChange}
          />
        )}

        {isStandard && (isAddMode || showField("emailBody", false)) && (
          <TextField
            label="E-post innehåll"
            value={orderData.emailBody}
            isEditable={isEditMode}
            onChange={handlers.onEmailBodyChange}
          />
        )}

        {showField("warehouseLocation", false) && (
          <TextField
            label="Lagerplats"
            value={orderData.warehouseLocation}
            isEditable={isEditMode}
            onChange={handlers.onWarehouseLocationChange}
          />
        )}

        {!isAddMode && (isCatalog || showField("supplier", false)) && (
          <SelectField
            label="Leverantör"
            value={isEditMode ? orderData.supplierId : orderData.supplier?.name}
            options={lists.suppliers || []}
            getOptionValue={(s) => s.id}
            getOptionLabel={(s) => s.name}
            isEditable={isEditMode && !!lists.suppliers}
            onChange={handlers.onSupplierChange}
            isLoading={loadingStates.isLoadingSuppliers}
            placeholder="- Ingen leverantör vald -"
          />
        )}

        {showField("arriveDate") && (
          <DateField
            label="Inkommen"
            value={orderData.arriveDate}
            isEditable={isEditMode}
            isAddMode={isAddMode}
            onChange={handlers.onArriveDateChange}
          />
        )}

        {showField("bookingDate") && (
          <DateField
            label="Bokad (prod.dag)"
            value={orderData.bookingDate}
            isEditable={isEditMode}
            isAddMode={isAddMode}
            onChange={handlers.onBookingDateChange}
          />
        )}

        {showField("deliveryDate") && (
          <DateField
            label="Leveransdatum"
            value={orderData.deliveryDate}
            isEditable={isEditMode}
            isAddMode={isAddMode}
            onChange={handlers.onDeliveryDateChange}
          />
        )}

        {showField("pickupTime", false) && (
          <DateField
            label="Upphämtningsdatum"
            value={orderData.pickupDate}
            isEditable={isEditMode}
            onChange={handlers.onPickupDateChange}
          />
        )}

        {showField("orderDate", false) && (
          <DateField
            label="Beställt"
            value={orderData.orderDate}
            isEditable={isEditMode}
            onChange={handlers.onOrderDateChange}
          />
        )}

        {isStandard && showField("fileName") && (
          <TextField
            label="Filnamn"
            value={orderData.filename}
            isEditable={isEditMode}
            onChange={handlers.onFilenameChange}
          />
        )}

        {isStandard && showField("carUrl", false) && (
          <Col xs={12} md={4}>
            <Form.Group className="mb-3">
              <Form.Label>PL URL</Form.Label>
              <div className="mt-2">
                {orderData.carUrl?.trim() ? (
                  <Link target="_blank" to={orderData.carUrl}>Länk</Link>
                ) : (
                  <span>-</span>
                )}
              </div>
            </Form.Group>
          </Col>
        )}
      </Row>

      {/* COMMENTS */}
      <Row>
        {showField("comment") && (
          <TextField
            label="Kommentar"
            value={orderData.comment}
            colSize={6}
            isEditable={isEditMode}
            onChange={handlers.onCommentChange}
            isTextarea
          />
        )}

        {showField("internalComment") && (
          <TextField
            label="Intern kommentar"
            value={orderData.internalComment}
            colSize={6}
            isEditable={isEditMode}
            onChange={handlers.onInternalCommentChange}
            isTextarea
          />
        )}
      </Row>

      {/* FLEET-SPECIFIC FIELDS */}
      {isFleet && (
        <>
          <Row>
            <TextField
              label="Meddelande på prisförslag"
              value={orderData.priceProposalMessage}
              colSize={6}
              isEditable={isEditMode}
              onChange={handlers.onPriceProposalMessageChange}
              isTextarea
            />
          </Row>
        </>
      )}

      {/* APPROVAL SWITCHES */}
      {(isCatalog || isFleet) && (
        <Row>
          <SwitchField
            label="Godkänd av kund"
            checked={orderData.approved || false}
            isEditable={isEditMode}
            onChange={handlers.onApprovedChange}
          />
        </Row>
      )}

      {isFleet && (
        <Row>
          <SwitchField
            label="Nekad av kund"
            checked={orderData.denied || false}
            isEditable={isEditMode}
            onChange={handlers.onDeniedChange}
          />
        </Row>
      )}

      {/* FLEET: POSTNORD FIELDS */}
      {isFleet && (
        <>
          <Row>
            <TextField
              label="Ordernummer hos Postnord"
              value={orderData.clientExternalOrderId}
              isEditable={isEditMode}
              onChange={handlers.onClientExternalOrderIdChange}
            />
            <TextField
              label="Miltal"
              value={orderData.odometer}
              isEditable={isAddMode}
              onChange={handlers.onOdometerChange}
            />
            <TextField
              label="Hämtad av"
              value={orderData.clientVehicleRetriever}
              isEditable={isAddMode}
              onChange={handlers.onClientVehicleRetrieverChange}
            />
          </Row>
          <Row>
            {isAddMode ? (
              <>
                <TextField
                  label="Verkstadens id"
                  value={orderData.workshopId}
                  isEditable
                  onChange={handlers.onWorkshopIdChange}
                />
                <TextField
                  label="Verkstadsnamn"
                  value={orderData.workshopName}
                  colSize={8}
                  isEditable
                  onChange={handlers.onWorkshopNameChange}
                />
              </>
            ) : (
              <TextField
                label="Verkstad"
                value={`${orderData.workshopName ?? ""} - ${orderData.workshopId ?? ""}`}
                colSize={6}
              />
            )}
          </Row>
        </>
      )}
    </>
  );

  return wrapInForm ? <Form>{content}</Form> : content;
};

export default OrderFields;
